import { Button, Dialog, DialogActions, Box, DialogContent, IconButton, DialogTitle, Typography, FormHelperText, TextField, RadioGroup, Radio, Alert, Divider } from '@mui/material';
import { setMachineReportDetailsData } from '../../../redux/machineReportDetailsDataSlice';
import { setSuccessfullModifyDialog } from '../../../redux/successfullModifyDialogSlice';
import { CustomSingleSelect } from '../../../utils/CustomSingleSelect';
import { CustomErrorLogger } from '../../../utils/CustomErrorLogger';
import { useDispatch, useSelector } from 'react-redux';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from '@mui/lab';
import Iconify from '../../Iconify';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import '../../../css/machines/addMachineReportDialog.css';

export const EditMachineReport = ({ editReportData, setEditReportData }) => {

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const machineReportDetailsData = useSelector((state) => state?.MachineReportDetailsData);

    const [errMsg, setErrMsg] = useState(null);
    const [loading, setLoading] = useState(false);

    const formik = useFormik({
        initialValues: {
            control_date: machineReportDetailsData?.control_date || '',
            company: { value: machineReportDetailsData?.company.id, label: machineReportDetailsData?.company.name, company_contact: machineReportDetailsData?.company.company_contact } || '',
            verifier: machineReportDetailsData?.verifier ? { value: machineReportDetailsData?.verifier.id, label: machineReportDetailsData?.verifier.name } : '',
            accompanist: machineReportDetailsData?.accompanist ? { value: machineReportDetailsData?.accompanist.id, label: machineReportDetailsData?.accompanist.first_name + ' ' + machineReportDetailsData?.accompanist.last_name } : '',
            status: machineReportDetailsData?.status || '',
            machine_id: machineReportDetailsData?.machine?.id || '',
        },

        validationSchema: Yup.object().shape({
            control_date: Yup.date().required(t('isRequired')),
            company: Yup.object().required(t('isRequired')),
            verifier: Yup.object().required(t('isRequired')),
            accompanist: Yup.object().required(t('isRequired')),
            status: Yup.string().required(t('isRequired')),
            machine_id: Yup.string().optional(),
        }),
        onSubmit: async (values) => {
            try {
                setLoading(true);
                const data = {
                    control_date: new Date(values.control_date),
                    company_id: values.company.value,
                    verifier_id: values.verifier.value,
                    accompanist_id: values.accompanist.value,
                    status: values.status,
                    ...(values.machine_id && { machine_id: values.machine_id }),
                }
                const response = await axios.patch(`machine-control/${machineReportDetailsData?.id}`, data);
                if (response.status === 200 || response.status === 201) {
                    const updatedData = {
                        ...machineReportDetailsData,
                        ...response.data,
                    };
                    dispatch(setMachineReportDetailsData(updatedData));
                    dispatch(setSuccessfullModifyDialog({ open: true, type: 'Modifier le rapport de la machine' }));
                    setLoading(false);
                    setEditReportData(false);
                }
            } catch (error) {
                setLoading(false);
                console.error('Error updating machine repport:', error);
                CustomErrorLogger(error, "error updating machine repport");
                setErrMsg(error?.response?.data?.message || error.message || 'erreur lors de la modification du rapport de la machine');
            }
        },
    });

    const { handleSubmit, values, touched, errors, setFieldValue, handleBlur } = formik;

    const fetchCompanies = useCallback(async (offset, limit, search) => {
        try {
            const response = await axios.get('company/findAllForReport', {
                params: {
                    offset,
                    limit,
                    filters: { debouncedSearch: search },
                },
            });
            if (response.status === 200 || response.status === 201) {
                return response.data.companiesData.map(company => ({
                    value: company.id,
                    label: company.name,
                    contacts: company.company_contact || [],
                }));
            }
        } catch (error) {
            console.error('Error fetching companies:', error);
            CustomErrorLogger(error, "error fetching companies in EditMachineReport");
        }
    }, []);

    const handleCreateCompanyContact = useCallback(async (newOption) => {
        try {
            const payload = {
                name: newOption.label,
                companyId: values.company.value,
            };
            const response = await axios.post('company-contact', payload);
            if (response.status === 200 || response.status === 201) {
                setFieldValue('verifier', { value: response.data.id, label: response.data.name });
            }
        } catch (error) {
            console.error('Error creating company-contact in EditMachineReport:', error);
            CustomErrorLogger(error, "Error creating company-contact in EditMachineReport");
        }
    }, [setFieldValue, values.company]);

    const fetchUsers = useCallback(async (offset, limit, search) => {
        try {
            const response = await axios.get('user', {
                params: {
                    offset,
                    limit,
                    filters: { debouncedSearch: search },
                },
            });
            if (response.status === 200 || response.status === 201) {
                return response.data.users.map(user => ({
                    value: user.id,
                    label: user.first_name + ' ' + user.last_name,
                }));
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            CustomErrorLogger(error, "error fetching users in EditMachineReport");
        }
    }, []);

    const onChangeCompany = (value) => {
        setFieldValue('company', value);
        setFieldValue('verifier', '');
    };


    const handleClose = useCallback(() => {
        setEditReportData(false);
    }, [setEditReportData]);

    return (
        <Dialog open={editReportData} fullWidth maxWidth="md">
            <IconButton onClick={handleClose} className='addReport_icon_button'>
                <Iconify icon="material-symbols:close" className='addReport_close_icon' />
            </IconButton>
            <DialogTitle className='addReport_dialog_title'>Modifier un rapport</DialogTitle>
            <Divider />
            {errMsg && <Alert severity="error">{errMsg}</Alert>}
            <form onSubmit={handleSubmit}>
                <DialogContent className='addReport_dialog_content'>
                    <Box className='addReport_helper_field_box'>
                        <FormHelperText id="outlined-weight-helper-text" className="addReportInputTitle">Entreprise associée</FormHelperText>
                        <CustomSingleSelect
                            placeholder='Sélectionnez une entreprise'
                            className='addReport_InputField'
                            required={true}
                            value={values.company}
                            fetchFunction={fetchCompanies}
                            backendSearchable={true}
                            onChange={onChangeCompany}
                            helperText={touched.company && errors.company}
                            error={touched.company && Boolean(errors.company)}
                        />
                    </Box>
                    {values.company &&
                        <Box className='addReport_helper_field_box'>
                            <FormHelperText id="outlined-weight-helper-text" className="addReportInputTitle">Vérificateur</FormHelperText>
                            <CustomSingleSelect
                                placeholder='Sélectionnez une Vérificateur'
                                className='addReport_InputField'
                                required={true}
                                value={values.verifier}
                                option={values.company.company_contact.map(contact => ({ value: contact.id, label: contact.name }))}
                                backendSearchable={true}
                                onChange={(value) => setFieldValue('verifier', value)}
                                creatable={true}
                                createFunction={handleCreateCompanyContact}
                                helperText={touched.verifier && errors.verifier}
                                error={touched.verifier && Boolean(errors.verifier)}
                            />
                        </Box>
                    }
                    <Box className='addReport_helper_field_box'>
                        <FormHelperText id="outlined-weight-helper-text" className="addReportInputTitle">Accompagnateur</FormHelperText>
                        <CustomSingleSelect
                            placeholder='Sélectionnez un accompagnateur'
                            className='addReport_InputField'
                            required={true}
                            value={values.accompanist}
                            fetchFunction={fetchUsers}
                            backendSearchable={true}
                            onChange={(value) => setFieldValue('accompanist', value)}
                            helperText={touched.accompanist && errors.accompanist}
                            error={touched.accompanist && Boolean(errors.accompanist)}
                        />
                    </Box>
                    <Box className="addReport_title_number_box">
                        <Box className='addReport_helper_field_box'>
                            <FormHelperText id="outlined-weight-helper-text" className="addReportInputTitle">Date du contrôle</FormHelperText>
                            <TextField
                                sx={{ width: '100%' }}
                                className={`addReport_InputField`}
                                id="outlined-adornment-weight"
                                type='date'
                                value={values.control_date ? new Date(values.control_date).toISOString().split('T')[0] : ''}
                                onChange={(event) => setFieldValue('control_date', event.target.value)}
                                onBlur={handleBlur}
                                name="control_date"
                                aria-describedby="outlined-weight-helper-text"
                                error={touched.control_date && Boolean(errors.control_date)}
                                helperText={touched.control_date && errors.control_date}
                            />
                        </Box>
                    </Box>
                    <Box className="addReport_title_number_box">
                        <Box className='addReport_helper_field_box'>
                            <FormHelperText id="outlined-weight-helper-text" className="addReportInputTitle">Statut de la machine</FormHelperText>
                            <RadioGroup
                                name="contractAssociation"
                                className='addReport_radio_group'
                                value={values.status}
                                onChange={(event) => setFieldValue('status', event.target.value)}
                            >
                                <Box className='addReport_radio_box'>
                                    <Radio value='Valide' />
                                    <Typography className="addReport_radio_label">Valide</Typography>
                                </Box>
                                <Box className='addReport_radio_box'>
                                    <Radio value='NonConforme' />
                                    <Typography className="addReport_radio_label">Non conforme</Typography>
                                </Box>
                                <Box className='addReport_radio_box'>
                                    <Radio value='AvecReparation' />
                                    <Typography className="addReport_radio_label">Avec réparation</Typography>
                                </Box>
                            </RadioGroup>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions className='addReport_dialog_actions'>
                    <Button className='addReport_button_annuler' onClick={handleClose}>{t('Annuler')}</Button>
                    <LoadingButton loading={loading} className='addReport_button_valider' variant="contained" type="submit">{t('Valider')}</LoadingButton>
                </DialogActions>
            </form>
        </Dialog>
    );
};